/**
 * CSV column mapper.
 *
 * Given a file, parses it and lets the user say what each column means.
 * The dropdown offers standard options (Email, name, company, title,
 * category…) plus location + business presets (city, state, phone, revenue,
 * etc.) that quietly persist to the lead's `custom_fields` JSONB. Anything
 * that doesn't fit a preset can still be mapped to an arbitrary custom
 * field with a user-named key, usable as {{that_key}} in email templates.
 *
 * Auto-suggests a mapping based on header aliases; the user can override
 * anything before confirming. Exactly one column must be marked Email.
 */
import Papa from 'papaparse';
import { AlertCircle, CheckCircle2, FileUp, Trash2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

export interface MappedRow {
  email: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  title?: string;
  description?: string;
  main_vertical?: string;
  custom_fields?: Record<string, string>;
}

// ─── Target model ────────────────────────────────────────────────────
// Every column maps to one of these. `system` targets land in native
// Lead columns; `full_name` splits into first_name + last_name; `custom`
// goes to custom_fields under `key`.

type SystemField = 'email' | 'first_name' | 'last_name' | 'company' | 'title' | 'description' | 'main_vertical';

type Target =
  | { type: 'skip' }
  | { type: 'system'; field: SystemField }
  | { type: 'full_name' }
  | { type: 'custom'; key: string };

interface OptionDef {
  /** Stable string used as the <select> option value + dropdown key. */
  value: string;
  /** Group label rendered as <optgroup>. */
  group: string;
  /** UI label shown in the dropdown. */
  label: string;
  /** What this option resolves to at build time. */
  target: Target;
  /** Header-name aliases used to auto-suggest this option. Match on
   *  normalized header (lowercased, non-alnum -> '_'). */
  aliases?: string[];
}

const OPTIONS: OptionDef[] = [
  // Identity
  { group: 'Identity', value: 'email', label: 'Email *', target: { type: 'system', field: 'email' }, aliases: ['email', 'email_address', 'e_mail', 'mail', 'work_email'] },
  { group: 'Identity', value: 'first_name', label: 'First name', target: { type: 'system', field: 'first_name' }, aliases: ['first_name', 'firstname', 'fname', 'given_name'] },
  { group: 'Identity', value: 'last_name', label: 'Last name', target: { type: 'system', field: 'last_name' }, aliases: ['last_name', 'lastname', 'lname', 'surname', 'family_name'] },
  { group: 'Identity', value: 'full_name', label: 'Full name (auto-split)', target: { type: 'full_name' }, aliases: ['full_name', 'fullname', 'name', 'contact_name', 'lead_name'] },
  { group: 'Identity', value: 'middle_name', label: 'Middle name', target: { type: 'custom', key: 'middle_name' }, aliases: ['middle_name', 'middlename', 'middle', 'mname'] },
  { group: 'Identity', value: 'title', label: 'Title', target: { type: 'system', field: 'title' }, aliases: ['title', 'job_title', 'role', 'position', 'jobtitle'] },
  { group: 'Identity', value: 'company', label: 'Company', target: { type: 'system', field: 'company' }, aliases: ['company', 'company_name', 'business', 'business_name', 'organization', 'org', 'employer', 'account'] },
  { group: 'Identity', value: 'description', label: 'Description', target: { type: 'system', field: 'description' }, aliases: ['description', 'desc', 'notes', 'about', 'summary', 'bio', 'about_company', 'company_description', 'business_description'] },
  { group: 'Identity', value: 'website', label: 'Website', target: { type: 'custom', key: 'website' }, aliases: ['website', 'url', 'domain', 'homepage', 'site', 'website_url', 'company_website'] },
  { group: 'Identity', value: 'linkedin', label: 'LinkedIn URL', target: { type: 'custom', key: 'linkedin' }, aliases: ['linkedin', 'linkedin_url', 'linkedin_profile'] },

  // Location
  { group: 'Location', value: 'city', label: 'City', target: { type: 'custom', key: 'city' }, aliases: ['city', 'town'] },
  { group: 'Location', value: 'state', label: 'State', target: { type: 'custom', key: 'state' }, aliases: ['state', 'region', 'province'] },
  { group: 'Location', value: 'street_address', label: 'Street address', target: { type: 'custom', key: 'street_address' }, aliases: ['street', 'address', 'street_address', 'address_line_1', 'street1', 'addr'] },
  { group: 'Location', value: 'postcode', label: 'Postcode', target: { type: 'custom', key: 'postcode' }, aliases: ['zip', 'zip_code', 'zipcode', 'postcode', 'postal_code', 'postal'] },
  { group: 'Location', value: 'country', label: 'Country', target: { type: 'custom', key: 'country' }, aliases: ['country', 'country_code', 'nation'] },

  // Business
  { group: 'Business', value: 'phone', label: 'Phone', target: { type: 'custom', key: 'phone' }, aliases: ['phone', 'phone_number', 'mobile', 'tel', 'telephone', 'cell', 'phone1', 'work_phone'] },
  { group: 'Business', value: 'main_vertical', label: 'Category / industry', target: { type: 'system', field: 'main_vertical' }, aliases: ['category', 'vertical', 'industry', 'niche', 'segment', 'main_vertical', 'business_type'] },
  { group: 'Business', value: 'revenue', label: 'Revenue', target: { type: 'custom', key: 'revenue' }, aliases: ['revenue', 'annual_revenue', 'sales', 'yearly_revenue'] },
  { group: 'Business', value: 'employee_size', label: 'Employee count', target: { type: 'custom', key: 'employee_size' }, aliases: ['employee_size', 'employees', 'headcount', 'company_size', 'employee_count', 'size', 'num_employees'] },
];

const OPTION_INDEX: Record<string, OptionDef> = Object.fromEntries(OPTIONS.map((o) => [o.value, o]));

const GROUP_ORDER = ['Identity', 'Location', 'Business'];

/** Header-alias → option value. Built once at module load. */
const ALIAS_TO_OPTION: Record<string, string> = (() => {
  const idx: Record<string, string> = {};
  for (const opt of OPTIONS) {
    for (const alias of opt.aliases ?? []) idx[alias] = opt.value;
  }
  return idx;
})();

// ─── Header handling ────────────────────────────────────────────────

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

/** Which dropdown option matches this header? Returns option value or null. */
function autoSuggest(header: string): string | null {
  const norm = normalizeHeader(header);
  return ALIAS_TO_OPTION[norm] ?? null;
}

// System fields (native Lead columns) — used to prevent double-mapping.
const SYSTEM_FIELDS: SystemField[] = ['email', 'first_name', 'last_name', 'company', 'title', 'description', 'main_vertical'];

// ─── Component ──────────────────────────────────────────────────────

interface Props {
  onConfirm: (rows: MappedRow[]) => void;
  onCancel?: () => void;
  confirmLabel?: string;
}

interface RowState {
  /** Dropdown selection value. 'skip', 'custom', or one of OPTIONS[].value. */
  option: string;
  /** Custom-field key when option === 'custom'. */
  customKey: string;
}

export function CsvMapper({ onConfirm, onCancel, confirmLabel = 'Import' }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Array<Record<string, string>>>([]);
  const [mapping, setMapping] = useState<Record<string, RowState>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState<string | null>(null);

  function reset(): void {
    if (inputRef.current) inputRef.current.value = '';
    setFileName(null);
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setParseError(null);
    setReviewError(null);
    setReviewNote(null);
  }

  function handleFile(file: File): void {
    setParseError(null);
    setReviewError(null);
    setReviewNote(null);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (out) => {
        if (!out.data || out.data.length === 0) {
          setParseError('The file has no rows.');
          return;
        }
        const rawHeaders = (out.meta.fields ?? []).filter(Boolean);
        if (rawHeaders.length === 0) {
          setParseError('No headers found. Make sure the first row of the CSV is column names.');
          return;
        }
        // Auto-suggest, but don't claim the same system field twice.
        const claimedSystem = new Set<SystemField>();
        const auto: Record<string, RowState> = {};
        for (const h of rawHeaders) {
          const guess = autoSuggest(h);
          if (guess) {
            const opt = OPTION_INDEX[guess];
            if (opt.target.type === 'system') {
              if (claimedSystem.has(opt.target.field)) {
                auto[h] = { option: 'skip', customKey: normalizeHeader(h) || 'field' };
                continue;
              }
              claimedSystem.add(opt.target.field);
            }
            auto[h] = { option: guess, customKey: '' };
          } else {
            auto[h] = { option: 'skip', customKey: normalizeHeader(h) || 'field' };
          }
        }
        setFileName(file.name);
        setHeaders(rawHeaders);
        setRawRows(out.data);
        setMapping(auto);
      },
      error: (err) => setParseError(err.message),
    });
  }

  const samples = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const h of headers) {
      const vals: string[] = [];
      for (const row of rawRows) {
        const v = (row[h] ?? '').trim();
        if (v) vals.push(v);
        if (vals.length === 3) break;
      }
      out[h] = vals;
    }
    return out;
  }, [headers, rawRows]);

  const emailHeader = headers.find((h) => mapping[h]?.option === 'email');

  /** Which system fields are currently claimed, by how many columns? */
  const systemClaims = useMemo(() => {
    const claims: Record<SystemField, string[]> = {
      email: [], first_name: [], last_name: [], company: [], title: [], description: [], main_vertical: [],
    };
    for (const h of headers) {
      const rs = mapping[h];
      if (!rs) continue;
      const opt = OPTION_INDEX[rs.option];
      if (!opt) continue;
      if (opt.target.type === 'system') claims[opt.target.field].push(h);
    }
    return claims;
  }, [headers, mapping]);
  const duplicatedSystem = SYSTEM_FIELDS.find((f) => systemClaims[f].length > 1);

  function setRow(header: string, next: Partial<RowState>): void {
    const cur = mapping[header] ?? { option: 'skip', customKey: '' };
    const merged: RowState = { ...cur, ...next };

    // If we're claiming a system field, evict any other column that had it.
    const opt = OPTION_INDEX[merged.option];
    if (opt?.target.type === 'system') {
      const claiming = opt.target.field;
      const nextMapping: Record<string, RowState> = { ...mapping };
      for (const h of headers) {
        if (h === header) continue;
        const other = nextMapping[h];
        if (!other) continue;
        const otherOpt = OPTION_INDEX[other.option];
        if (otherOpt?.target.type === 'system' && otherOpt.target.field === claiming) {
          nextMapping[h] = { ...other, option: 'skip' };
        }
      }
      nextMapping[header] = merged;
      setMapping(nextMapping);
    } else {
      setMapping({ ...mapping, [header]: merged });
    }
    setReviewError(null);
    setReviewNote(null);
  }

  function build(): void {
    setReviewError(null);
    setReviewNote(null);
    if (!emailHeader) {
      setReviewError('Pick which column is the Email. Every lead needs an email.');
      return;
    }
    // Custom-field keys must be non-empty, valid identifiers, and unique.
    const seenCustomKeys = new Set<string>();
    for (const h of headers) {
      const rs = mapping[h];
      if (!rs || rs.option !== 'custom') continue;
      const key = rs.customKey.trim();
      if (!key) {
        setReviewError(`Give the custom field for "${h}" a key name (or set it to Skip).`);
        return;
      }
      if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key)) {
        setReviewError(`Custom key "${key}" must start with a letter/underscore and use only letters/digits/underscores.`);
        return;
      }
      if (seenCustomKeys.has(key)) {
        setReviewError(`Two columns use the custom key "${key}". Each key must be unique.`);
        return;
      }
      seenCustomKeys.add(key);
    }
    // A preset that resolves to a custom key could clash with a user-typed one.
    for (const h of headers) {
      const rs = mapping[h];
      if (!rs) continue;
      const opt = OPTION_INDEX[rs.option];
      if (opt?.target.type === 'custom') {
        if (seenCustomKeys.has(opt.target.key)) {
          setReviewError(
            `Custom key "${opt.target.key}" is used by both a preset (${opt.label}) and a manual Custom field.`,
          );
          return;
        }
        seenCustomKeys.add(opt.target.key);
      }
    }

    // Resolve each row using the mapping.
    const built: MappedRow[] = [];
    let dropped = 0;
    for (const row of rawRows) {
      const email = (row[emailHeader] ?? '').trim().toLowerCase();
      if (!email || !/@/.test(email)) {
        dropped++;
        continue;
      }
      const out: MappedRow = { email };
      const customs: Record<string, string> = {};
      for (const h of headers) {
        const rs = mapping[h];
        if (!rs || rs.option === 'skip') continue;
        const raw = (row[h] ?? '').trim();
        if (!raw) continue;
        if (rs.option === 'custom') {
          customs[rs.customKey.trim()] = raw;
          continue;
        }
        const opt = OPTION_INDEX[rs.option];
        if (!opt) continue;
        if (opt.target.type === 'full_name') {
          const [first, ...rest] = raw.split(/\s+/);
          if (first) out.first_name = out.first_name || first;
          const last = rest.join(' ').trim();
          if (last) out.last_name = out.last_name || last;
        } else if (opt.target.type === 'system') {
          if (opt.target.field === 'email') continue; // already handled above
          (out as unknown as Record<string, string>)[opt.target.field] = raw;
        } else if (opt.target.type === 'custom') {
          customs[opt.target.key] = raw;
        }
      }
      if (Object.keys(customs).length > 0) out.custom_fields = customs;
      built.push(out);
    }
    if (built.length === 0) {
      setReviewError(
        `No valid rows — every row had a blank or malformed email under "${emailHeader}".`,
      );
      return;
    }
    if (dropped > 0) {
      setReviewNote(
        `${dropped} row${dropped === 1 ? '' : 's'} skipped for missing/invalid email. Importing ${built.length}.`,
      );
    }
    onConfirm(built);
  }

  // ─── Render ────────────────────────────────────────────────────

  if (headers.length === 0) {
    return (
      <div className="space-y-2">
        <label className="text-xs font-medium text-mb-text uppercase tracking-wider block">
          CSV file
        </label>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
          className="mb-input w-full file:mr-3 file:rounded file:border-0 file:bg-mb-accent-soft file:px-3 file:py-1 file:text-sm file:font-medium file:text-mb-accent-press hover:file:bg-mb-accent-soft/70"
        />
        <p className="text-xs text-mb-text-muted">
          Once you upload, each column gets a dropdown to map it — Email,
          name, title, company, phone, address, custom field, or skip. Anything
          you don't want lands in <span className="font-mono">Skip</span> and is
          ignored on import.
        </p>
        {parseError ? (
          <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
            {parseError}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* File header */}
      <div className="flex items-center gap-2 rounded-md border border-mb-border bg-mb-surface p-2 text-xs">
        <FileUp className="h-4 w-4 text-mb-accent-press" />
        <span className="font-medium text-mb-text">{fileName}</span>
        <span className="text-mb-text-muted">
          · {rawRows.length} row{rawRows.length === 1 ? '' : 's'} · {headers.length} column
          {headers.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={reset}
          className="ml-auto text-mb-text-muted hover:text-mb-danger"
          aria-label="Remove file"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Mapping table */}
      <div className="rounded-md border border-mb-border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-mb-surface">
            <tr className="text-left text-mb-text-muted uppercase tracking-wider">
              <th className="px-2.5 py-2 font-medium">CSV column</th>
              <th className="px-2.5 py-2 font-medium">Sample values</th>
              <th className="px-2.5 py-2 font-medium w-64">Map to</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((h) => {
              const rs = mapping[h];
              const isCustom = rs?.option === 'custom';
              const dupSystem = duplicatedSystem &&
                systemClaims[duplicatedSystem].includes(h);
              return (
                <tr key={h} className="border-t border-mb-border">
                  <td className="px-2.5 py-2 font-mono text-mb-text align-top">{h}</td>
                  <td className="px-2.5 py-2 text-mb-text-muted align-top">
                    {samples[h].length === 0 ? (
                      <span className="italic text-mb-text-subtle">(empty)</span>
                    ) : (
                      samples[h].map((v, i) => (
                        <div key={i} className="truncate max-w-[16rem]">
                          {v}
                        </div>
                      ))
                    )}
                  </td>
                  <td className="px-2.5 py-2 align-top">
                    <select
                      className="mb-input text-xs py-1"
                      value={rs?.option ?? 'skip'}
                      onChange={(e) => setRow(h, { option: e.target.value })}
                    >
                      <option value="skip">Skip (ignore this column)</option>
                      {GROUP_ORDER.map((group) => (
                        <optgroup key={group} label={group}>
                          {OPTIONS.filter((o) => o.group === group).map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      <optgroup label="Advanced">
                        <option value="custom">Custom field…</option>
                      </optgroup>
                    </select>
                    {isCustom ? (
                      <div className="mt-1.5">
                        <input
                          type="text"
                          className="mb-input text-xs py-1 font-mono"
                          placeholder="custom_field_key"
                          value={rs.customKey}
                          onChange={(e) => setRow(h, { customKey: e.target.value.trim() })}
                        />
                        <div className="mt-0.5 text-[10px] text-mb-text-subtle">
                          Use in templates as{' '}
                          <code className="text-mb-accent-press">
                            {'{{'}
                            {rs.customKey || 'key'}
                            {'}}'}
                          </code>
                        </div>
                      </div>
                    ) : null}
                    {dupSystem ? (
                      <div className="mt-1 text-[10px] text-mb-danger flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Two columns mapped to {duplicatedSystem} — only one allowed
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary strip */}
      <div className="rounded-md border border-mb-border bg-mb-surface p-2 text-[11px] text-mb-text-muted">
        <div className="flex items-center gap-2">
          {emailHeader ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-mb-success" />
              Email column: <b className="font-mono">{emailHeader}</b>
            </>
          ) : (
            <>
              <AlertCircle className="h-3.5 w-3.5 text-mb-danger" />
              Pick which column is the Email before importing.
            </>
          )}
        </div>
      </div>

      {reviewError ? (
        <div role="alert" className="rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
          {reviewError}
        </div>
      ) : null}
      {reviewNote ? (
        <div className="rounded-md border border-mb-accent/30 bg-mb-accent-soft p-2 text-xs text-mb-accent-press">
          {reviewNote}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        {onCancel ? (
          <button type="button" className="mb-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="mb-btn-primary"
          disabled={!emailHeader || Boolean(duplicatedSystem)}
          onClick={build}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
